import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';

const FranchiseManagement = () => {
    const [franchises, setFranchises] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        ownerId: '',
        pincodesCovered: '',
        address: '',
        city: '',
        state: '',
        commissionRate: 0
    });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            const [franchiseRes, userRes] = await Promise.all([
                apiClient.get('/franchise', { headers }),
                apiClient.get('/users', { headers })
            ]);
            
            setFranchises(franchiseRes.data);
            setUsers(userRes.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({
            name: '', ownerId: '', pincodesCovered: '', address: '', city: '', state: '', commissionRate: 0
        });
    };

    const handleEditClick = (franchise) => {
        setEditingId(franchise._id);
        setFormData({
            name: franchise.name || '',
            ownerId: franchise.owner?._id || '',
            pincodesCovered: (franchise.pincodesCovered || []).join(', '),
            address: franchise.location?.address || '',
            city: franchise.location?.city || '',
            state: franchise.location?.state || '',
            commissionRate: franchise.commissionRate || 0
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteFranchise = async (franchise) => {
        if (!window.confirm(`Delete franchise "${franchise.name}"? Its owner account will revert to a regular customer.`)) return;
        try {
            const token = localStorage.getItem('token');
            await apiClient.delete(`/franchise/${franchise._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Franchise deleted successfully!');
            fetchData();
        } catch (error) {
            alert(error.response?.data?.message || 'Error deleting franchise');
            console.error(error);
        }
    };

    const handleAddFranchise = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const pincodes = formData.pincodesCovered.split(',').map(p => p.trim()).filter(Boolean);

            const payload = {
                name: formData.name,
                pincodesCovered: pincodes,
                location: {
                    address: formData.address,
                    city: formData.city,
                    state: formData.state
                },
                commissionRate: formData.commissionRate
            };

            if (editingId) {
                await apiClient.put(`/franchise/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert('Franchise updated successfully!');
            } else {
                await apiClient.post('/franchise', { ...payload, ownerId: formData.ownerId }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert('Franchise created successfully!');
            }

            fetchData();
            resetForm();
        } catch (error) {
            alert(error.response?.data?.message || `Error ${editingId ? 'updating' : 'creating'} franchise`);
            console.error(error);
        }
    };

    const toggleStatus = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await apiClient.put(`/franchise/${id}/toggle-status`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchData();
        } catch (error) {
            alert('Error updating status');
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="p-4 md:p-6 bg-white shadow-md rounded-lg max-w-full overflow-hidden">
            <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-8 w-2 bg-green-500 rounded-full"></div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight text-center">
                    Manage Franchises
                </h2>
            </div>

            <div className="mb-10 bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold mb-4 text-gray-700">{editingId ? 'Edit Franchise' : 'Add New Franchise'}</h3>
                <form onSubmit={handleAddFranchise} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Franchise Name (e.g. Rohtak Sector 2)" className="p-2 border rounded" required />

                    <select name="ownerId" value={formData.ownerId} onChange={handleInputChange} className="p-2 border rounded disabled:bg-gray-100 disabled:text-gray-400" required={!editingId} disabled={!!editingId}>
                        <option value="">{editingId ? 'Owner (fixed at creation)' : 'Select Owner Account'}</option>
                        {users.map(u => (
                            <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                        ))}
                    </select>

                    <input type="text" name="pincodesCovered" value={formData.pincodesCovered} onChange={handleInputChange} placeholder="Pincodes Covered (comma separated)" className="p-2 border rounded" required />
                    <input type="number" name="commissionRate" value={formData.commissionRate} onChange={handleInputChange} placeholder="Commission Rate (%)" className="p-2 border rounded" required />
                    <input type="text" name="address" value={formData.address} onChange={handleInputChange} placeholder="Street Address" className="p-2 border rounded" />
                    <div className="flex gap-2">
                        <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" className="p-2 border rounded w-1/2" />
                        <input type="text" name="state" value={formData.state} onChange={handleInputChange} placeholder="State" className="p-2 border rounded w-1/2" />
                    </div>

                    <div className="md:col-span-2 mt-2 flex gap-3">
                        <button type="submit" className="flex-1 bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700">
                            {editingId ? 'Save Changes' : 'Create Franchise'}
                        </button>
                        {editingId && (
                            <button type="button" onClick={resetForm} className="px-8 bg-gray-300 text-gray-700 font-bold py-3 rounded-lg hover:bg-gray-400">
                                Cancel
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <h3 className="text-xl font-bold mb-4 text-gray-700">Existing Franchises</h3>
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-100 border-b">
                            <th className="p-3 font-semibold">Name</th>
                            <th className="p-3 font-semibold">Owner</th>
                            <th className="p-3 font-semibold">Pincodes</th>
                            <th className="p-3 font-semibold">Performance</th>
                            <th className="p-3 font-semibold">Status</th>
                            <th className="p-3 font-semibold">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {franchises.map(f => (
                            <tr key={f._id} className="border-b hover:bg-gray-50">
                                <td className="p-3 font-medium">{f.name}</td>
                                <td className="p-3">{f.owner?.name} <br/><span className="text-sm text-gray-500">{f.owner?.email}</span></td>
                                <td className="p-3 text-sm max-w-[200px] truncate" title={f.pincodesCovered?.join(', ')}>
                                    {f.pincodesCovered?.join(', ')}
                                </td>
                                <td className="p-3 text-sm whitespace-nowrap">
                                    <div className="font-bold text-green-700">₹{(f.stats?.revenue || 0).toLocaleString('en-IN')}</div>
                                    <div className="text-xs text-gray-500">{f.stats?.orders || 0} orders · {f.stats?.products || 0} products</div>
                                </td>
                                <td className="p-3">
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${f.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {f.status.toUpperCase()}
                                    </span>
                                </td>
                                <td className="p-3">
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => handleEditClick(f)}
                                            className="px-3 py-1 rounded text-sm text-white bg-blue-500 hover:bg-blue-600"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => toggleStatus(f._id)}
                                            className={`px-3 py-1 rounded text-sm text-white ${f.status === 'active' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
                                        >
                                            {f.status === 'active' ? 'Deactivate' : 'Activate'}
                                        </button>
                                        <button
                                            onClick={() => handleDeleteFranchise(f)}
                                            className="px-3 py-1 rounded text-sm text-white bg-gray-700 hover:bg-gray-800"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {franchises.length === 0 && (
                            <tr>
                                <td colSpan="6" className="p-4 text-center text-gray-500">No franchises found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default FranchiseManagement;
